import { CircularProgress } from '@material-ui/core';
import type { ECElementEvent, EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import React, { JSX, useEffect, useRef, useState } from 'react';

import { IconTextButtonV2 } from '~/src/dash/components/buttons/IconTextButtonV2';
import {
  IEChartsSankeyLabelParams,
  IEChartsSankeyLink,
  IEChartsSankeyNode,
  ISankeyNodeConfig,
  ISankeyNodeData,
} from '~/src/dash/components/charts/Sankey/common';
import { FONT_SIZE_PIXELS, FONT_WEIGHT } from '~/src/dash/components/text/common';
import {
  Col,
  Cols,
  ITProps,
  Row,
  Rows,
  spacing,
  Text,
  TKey,
  TooltipWithTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { IconV2 } from '~/src/dash/components/icons/IconV2';

const emptyChartStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
};

// Font size constants (in pixels)
const DEFAULT_FONT_SIZE = FONT_SIZE_PIXELS.small;
const MIN_FONT_SIZE = FONT_SIZE_PIXELS.mini;

// Calculate responsive font size based on container width
const calculateFontSize = (containerWidth: number): number => {
  const minWidth = 600;
  const maxWidth = 1200;

  if (containerWidth <= minWidth) return MIN_FONT_SIZE;
  if (containerWidth >= maxWidth) return DEFAULT_FONT_SIZE;

  // Linear interpolation between min and max
  const ratio = (containerWidth - minWidth) / (maxWidth - minWidth);
  return Math.round(MIN_FONT_SIZE + ratio * (DEFAULT_FONT_SIZE - MIN_FONT_SIZE));
};

// Tooltip formatter params for Sankey charts
interface SankeyTooltipParams {
  dataType: 'node' | 'edge';
  name: string;
  value: number;
  data: {
    source?: string;
    target?: string;
    value?: number;
    originalValue?: number;
    nodeData?: ISankeyNodeData;
  };
}

interface IGetOptionProps {
  nodes: IEChartsSankeyNode[];
  links: IEChartsSankeyLink[];
  labelFormatter: (params: IEChartsSankeyLabelParams) => string;
  nodeTooltipFormatter: (
    nodeName: string,
    value: number,
    nodeConfigMap: Record<string, ISankeyNodeConfig>,
    nodeData?: ISankeyNodeData,
  ) => string;
  linkTooltipFormatter: (
    source: string,
    target: string,
    nodeConfigMap: Record<string, ISankeyNodeConfig>,
    value: number,
  ) => string;
  nodeConfigMap: Record<string, ISankeyNodeConfig>;
  layoutIterations?: number;
  fontSize: number;
}

// ECharts configuration with better tooltip positioning
const getOption = ({
  nodes,
  links,
  labelFormatter,
  nodeTooltipFormatter,
  linkTooltipFormatter,
  nodeConfigMap,
  layoutIterations = 0,
  fontSize,
}: IGetOptionProps): EChartsOption => ({
  tooltip: {
    trigger: 'item',
    triggerOn: 'mousemove',
    confine: true,
    renderMode: 'html',
    position: (point: number[]) => {
      return [Math.round(point[0] + 20), Math.round(point[1] + 10)];
    },
    // ECharts expects generic tooltip formatter, but we use custom interface for type safety
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    formatter: ((params: SankeyTooltipParams) => {
      if (params.dataType === 'edge') {
        const originalValue = params.data.originalValue;
        const displayValue = originalValue ?? params.data.value ?? 0;
        return linkTooltipFormatter(
          params.data.source ?? '',
          params.data.target ?? '',
          nodeConfigMap,
          displayValue,
        );
      } else {
        return nodeTooltipFormatter(params.name, params.value, nodeConfigMap, params.data.nodeData);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as any,
    backgroundColor: publicaColors.white,
    borderColor: publicaColors.lightGray,
    borderWidth: 1,
    textStyle: {
      color: publicaColors.veryDarkGray,
      fontSize,
      fontWeight: FONT_WEIGHT.base,
      fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    },
    extraCssText:
      "max-width: none; \
      width: max-content; \
      box-shadow: 0 2px 8px rgba(0,0,0,0.15); \
      padding: 8px 12px; \
      font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; \
      font-size: 14px; \
      -webkit-font-smoothing: antialiased; \
      -moz-osx-font-smoothing: grayscale; \
      text-rendering: optimizeLegibility;",
  },
  series: [
    {
      type: 'sankey',
      emphasis: {
        focus: 'adjacency',
      },
      data: nodes,
      links,
      orient: 'horizontal',
      nodeAlign: 'left', // Respects node depths - terminals don't go to the end
      nodeWidth: 12, // Reduced for less blocky appearance
      nodeGap: 40, // Increased spacing to accommodate multi-line labels with more separation
      layoutIterations, // MUST be 0 to respect node ordering
      label: {
        formatter: labelFormatter,
        position: 'right',
        color: publicaColors.veryDarkGray,
        fontSize,
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        rich: {
          labelName: {
            color: publicaColors.veryDarkGray,
            fontSize,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
          },
          labelValue: {
            color: publicaColors.gray,
            fontSize,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            padding: [2, 0, 0, 0], // Add 2 pixels of top padding for spacing
          },
        },
      },
      lineStyle: {
        color: 'gradient',
        opacity: 0.4,
        curveness: 0.75, // Maximum curve for visual appeal (0-1 range)
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
  ],
});

interface IProps {
  titleTKey: TKey;
  titleTooltipTKey?: TKey;
  chartHeight?: number;
  loadState: LoadingState;
  nodes: IEChartsSankeyNode[];
  links: IEChartsSankeyLink[];
  labelFormatter: (params: IEChartsSankeyLabelParams) => string;
  nodeTooltipFormatter: (
    nodeName: string,
    value: number,
    nodeConfigMap: Record<string, ISankeyNodeConfig>,
    nodeData?: ISankeyNodeData,
  ) => string;
  linkTooltipFormatter: (
    source: string,
    target: string,
    nodeConfigMap: Record<string, ISankeyNodeConfig>,
    value: number,
  ) => string;
  nodeConfigMap: Record<string, ISankeyNodeConfig>;
  layoutIterations?: number;
  customNoDataMessageTKey?: TKey;
  onNodeClick?: (params: ECElementEvent) => void;
  onLinkClick?: (params: { data: IEChartsSankeyLink }) => void;
  onResetDrilldown?: () => void;
  isResetActive?: boolean;
  messageTKey?: TKey;
  messageTProps?: ITProps;
}

export const Sankey = React.memo(
  ({
    titleTKey,
    titleTooltipTKey,
    chartHeight = 320,
    loadState,
    nodes,
    links,
    labelFormatter,
    nodeTooltipFormatter,
    linkTooltipFormatter,
    nodeConfigMap,
    layoutIterations = 0,
    customNoDataMessageTKey,
    onNodeClick,
    onLinkClick,
    onResetDrilldown = () => null,
    isResetActive = false,
    messageTKey,
    messageTProps = {},
  }: IProps): JSX.Element => {
    const height = `${chartHeight}px`;
    const hasData = nodes.length > 0 && links.length > 0;
    const containerRef = useRef<HTMLDivElement>(null);
    const [fontSize, setFontSize] = useState<number>(DEFAULT_FONT_SIZE);

    // Set up resize observer to dynamically adjust font size
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          const newFontSize = calculateFontSize(width);
          setFontSize(newFontSize);
        }
      });

      resizeObserver.observe(container);

      // Initial calculation
      const initialWidth = container.getBoundingClientRect().width;
      setFontSize(calculateFontSize(initialWidth));

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    const renderContent = (): JSX.Element => {
      if (loadState === LoadingStateLoading) {
        return (
          <div style={emptyChartStyle}>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <Text tKey='LoadingQuery' />
              </Col>
              <Col>
                <CircularProgress size={14} />
              </Col>
            </Cols>
          </div>
        );
      }

      if (!hasData) {
        return (
          <div style={emptyChartStyle}>
            <Text tKey={customNoDataMessageTKey || 'NoDataToDisplay'} />
          </div>
        );
      }

      return (
        <ReactECharts
          option={getOption({
            nodes,
            links,
            labelFormatter,
            nodeTooltipFormatter,
            linkTooltipFormatter,
            nodeConfigMap,
            layoutIterations,
            fontSize,
          })}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'svg' }}
          onEvents={{
            click: (params: ECElementEvent) => {
              if (params.dataType === 'node' && onNodeClick) {
                onNodeClick(params);
              } else if (params.dataType === 'edge' && onLinkClick) {
                onLinkClick({ data: params.data as IEChartsSankeyLink });
              }
            },
          }}
        />
      );
    };

    return (
      <Rows paddingBottom={spacing.small}>
        <Row noPadding marginBottom={spacing.base}>
          <Cols style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Col>
              <Rows>
                <Row noPadding>
                  <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                    <Col>
                      <Text bold tKey={titleTKey} style={{ lineHeight: 1.2 }} />
                    </Col>
                    {titleTooltipTKey && (
                      <Col displayFlex>
                        <TooltipWithTranslation tKey={titleTooltipTKey}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <IconV2 name='info-static' color={publicaColors.veryDarkGray} />
                          </div>
                        </TooltipWithTranslation>
                      </Col>
                    )}
                  </Cols>
                </Row>
                {messageTKey && (
                  <Row noPadding>
                    <Col>
                      <Text extraSmall tKey={messageTKey} tProps={messageTProps} />
                    </Col>
                  </Row>
                )}
              </Rows>
            </Col>
            {onResetDrilldown && (
              <Col>
                <IconTextButtonV2
                  iconSlug='undo'
                  tKey='Reset'
                  onClick={onResetDrilldown}
                  disabled={!isResetActive}
                />
              </Col>
            )}
          </Cols>
        </Row>
        <Row noPadding style={{ height }}>
          <div ref={containerRef} style={{ height: '100%', width: '100%' }}>
            {renderContent()}
          </div>
        </Row>
      </Rows>
    );
  },
);
